import { useEffect, useRef, useState } from 'react';
import type {
  ExamPreviewOfficialComment,
  OfficialCommentBody,
} from '../../domain/entities/content.entity';
import { useContentItem } from '../hooks/use-content-items';
import { useSaveOfficialComment } from '../hooks/use-official-comment';
import {
  type AttachedImage,
  attachedFilenames,
  editorHtmlToTokens,
  openImageFilePicker,
  preflightChosenFile,
  preparedToBase64,
  richTextIsEmpty,
  toDataUri,
  toImagePayload,
  tokensToEditorHtml,
  uniqueAttachmentName,
} from '@react-apps/content-editor';
import { describeIssue } from '../image-preflight-messages';
import { getApiErrorMessage } from '../display';
import { commentOrigin, markdownToEditorHtml } from '../harrison';
import { ConfirmModal } from './ConfirmModal';
import { HarrisonCommentAssistant } from './HarrisonCommentAssistant';
import { RichTextEditor, type PickedImage } from './RichTextEditor';

/**
 * O que o form do comentário usa para perguntar "mudou?" e mandar salvar.
 * Mesmo padrão do `buildRef` do `BodyForm`: o campo é dono do seu estado, e o
 * pai só pergunta "está sujo?" e manda salvar.
 */
export interface OfficialCommentHandle {
  isDirty: () => boolean;
  /**
   * **Propaga** a falha em vez de devolvê-la como `false` — ver o mesmo
   * contrato (e o porquê) em `ClassificationHandle`. A mensagem inline
   * continua saindo daqui pelo `save.error`.
   */
  save: () => Promise<void>;
}
export type OfficialCommentRef = { current: OfficialCommentHandle | null };

/**
 * Comentário oficial da questão, no form da seção dele — **um por questão**,
 * então não há lista, escolha nem "adicionar outro": é um campo.
 *
 * Duas coisas que não são óbvias e não podem mudar:
 *
 * **1. O texto é buscado cru, não reaproveitado do preview.** No preview cada
 * `{{img:<fileId>}}` já virou `<img src="…">`, e `img` não está no allowlist de
 * escrita do back — salvar aquele HTML apagaria as imagens do comentário. É a
 * mesma armadilha que já obriga o corpo da questão a rebuscar o item.
 *
 * **2. Salvar acontece pelo botão do form da seção, não por um botão daqui.**
 * O campo não sabe do lock nem da seção: quem o monta decide quando salvar.
 * Já foi o "Salvar" único do form da questão; com o comentário na seção
 * própria, cada seção tem o seu, e a dúvida de qual salva o quê não existe.
 *
 * **3. Autor não se digita.** Quem salva assina, e o servidor resolve isso do
 * token — pedir o nome num campo seria pedir para digitar o que ele já sabe, e
 * abrir espaço para digitar errado. O crédito atual aparece como texto; ao
 * salvar, passa a ser de quem salvou. (Na importação por CSV é diferente: lá o
 * nome vem da planilha, porque quem sobe a prova pode estar importando o
 * comentário de outra pessoa do time.)
 *
 * **4. O Harrison escreve no mesmo editor** (DESEN-34). O rascunho entra
 * remontando o editor por `key`, porque o TipTap só lê `value` no mount; com
 * texto no editor, pede confirmação antes de trocar. O salvar declara a origem
 * (`commentOrigin`): `HARRISON` enquanto houver rascunho aplicado e o editor
 * não tiver sido zerado. O campo só existe com a questão em edição, e portanto
 * com o lock dela: o botão não tem lock próprio.
 */
export function OfficialCommentField({
  questionId,
  comment,
  imageUrlsByFileId,
  onDirtyChange,
  onGeneratingChange,
  harrisonDisabled = false,
  fieldRef,
}: {
  questionId: string;
  /** O que o preview trouxe: id, autor e o texto **renderizado**. */
  comment: ExamPreviewOfficialComment | null;
  imageUrlsByFileId: Record<string, string>;
  onDirtyChange: (dirty: boolean) => void;
  /** O Harrison está escrevendo: quem monta o campo trava o seu "Salvar". */
  onGeneratingChange?: (generating: boolean) => void;
  /** Sem lock o texto fica para copiar, mas o Harrison não escreve mais. */
  harrisonDisabled?: boolean;
  fieldRef: OfficialCommentRef;
}) {
  const save = useSaveOfficialComment(questionId);
  // Só busca quando já existe comentário — questão sem comentário não paga
  // requisição nenhuma por abrir o form.
  const raw = useContentItem(comment?.id);

  const [text, setText] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [issue, setIssue] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(comment === null);
  // Trocar a `key` remonta o editor com o `text` atual: é o único jeito de um
  // rascunho do Harrison aparecer num TipTap que já montou.
  const [editorKey, setEditorKey] = useState(0);
  const [draftApplied, setDraftApplied] = useState(false);
  // Rascunho que chegou com texto no editor, esperando a confirmação.
  const [pendingDraft, setPendingDraft] = useState<string | null>(null);
  // Aviso próprio do Harrison: vale até a próxima tecla ou a próxima geração,
  // ao contrário do `issue` da imagem.
  const [draftIssue, setDraftIssue] = useState<string | null>(null);

  // O que está gravado, para comparar com o rascunho. Sem comentário, é o
  // vazio. Era gravado num efeito à parte, "o primeiro texto não vazio depois
  // de carregar" — e, numa questão sem comentário, esse texto era a primeira
  // tecla digitada: o campo se dizia sem alteração e o comentário novo não era
  // salvo.
  const initial = useRef('');

  // O texto cru chega depois (é outra requisição). Semeia uma vez: um `useState`
  // com valor inicial não veria a resposta, e sincronizar a cada render
  // sobrescreveria o que a pessoa já digitou.
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || !raw.data) return;
    seeded.current = true;
    const body = raw.data.item.body as OfficialCommentBody;
    const html = tokensToEditorHtml(body.text ?? '', imageUrlsByFileId);
    initial.current = html;
    setText(html);
    setLoaded(true);
  }, [raw.data, imageUrlsByFileId]);

  const dirty = loaded && text !== initial.current;

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const pickImage = async (): Promise<PickedImage | null> => {
    const file = await openImageFilePicker();
    if (!file) return null;
    const result = await preflightChosenFile(file.name, [], file);
    if ('issue' in result) {
      setIssue(describeIssue(result.issue));
      return null;
    }
    setIssue(null);
    const base64 = await preparedToBase64(result.prepared);
    const previewUrl = toDataUri(base64, result.prepared.mime);
    // Nome único: dois prints colados são dois `image.png`, e o segundo
    // sobrescreveria os bytes do primeiro nas duas figuras.
    const filename = uniqueAttachmentName(
      file.name,
      images.map((image) => image.filename),
    );
    setImages((prev) => [...prev, { filename, base64, previewUrl }]);
    return { filename, src: previewUrl };
  };

  // O texto de agora, não o do render em que se clicou em gerar: a geração
  // leva até um minuto, o editor segue editável, e decidir pelo texto velho
  // trocava sem confirmação o que a pessoa digitou enquanto esperava.
  const textRef = useRef(text);
  textRef.current = text;
  // Lock perdido no meio da geração: o rascunho que chega não entra mais.
  const disabledRef = useRef(harrisonDisabled);
  disabledRef.current = harrisonDisabled;

  const applyDraft = (html: string) => {
    setText(html);
    setEditorKey((key) => key + 1);
    setDraftApplied(true);
  };

  // Zerar o editor desfaz o rascunho: o que for escrito depois é da pessoa,
  // não do Harrison.
  const onEditorChange = (html: string) => {
    // Antes do `setText`: o TipTap chama isto fora de evento React, e o render
    // que atualizaria o ref pode vir depois da resposta do Harrison.
    textRef.current = html;
    setDraftIssue(null);
    setText(html);
    if (!hasContent(html)) setDraftApplied(false);
  };

  const onGenerated = (markdown: string) => {
    setDraftIssue(null);
    if (disabledRef.current) return;
    const html = markdownToEditorHtml(markdown);
    // Rascunho vazio só apagaria o que está no editor.
    if (!hasContent(html)) {
      setDraftIssue('O Harrison devolveu um rascunho vazio. Tente de novo.');
      return;
    }
    if (hasContent(textRef.current)) setPendingDraft(html);
    else applyDraft(html);
  };

  const commit = async (): Promise<void> => {
    const tokens = editorHtmlToTokens(text);
    await save.mutateAsync({
      text: tokens,
      // Só sobe o que o texto cita — imagem cujo nó foi apagado não vai, e o
      // back recusaria "arquivo sem token" se fosse. A pergunta é feita ao
      // HTML do editor, onde a figura é nó com `data-filename`: no texto já em
      // token não há nó, e o `images[]` saía vazio.
      images: toImagePayload(images, attachedFilenames([text])),
      origin: commentOrigin({ draftApplied, editorEmpty: !hasContent(text) }),
    });
    // Só depois de gravado: com o `initial` movido antes, uma falha deixaria o
    // campo dizendo "sem alteração" com a alteração ainda por salvar.
    initial.current = text;
  };

  // O card chama estes dois no seu próprio `save()`. O `isDirty` relê o
  // `initial` na hora: no render que semeia o texto ele ainda está vazio, e um
  // `dirty` capturado ali diria "alterado" a quem só abriu o form, sem nenhum
  // render depois para corrigir — o `PUT` passaria o crédito a essa pessoa.
  fieldRef.current = {
    isDirty: () => loaded && text !== initial.current,
    save: commit,
  };

  return (
    <div className="space-y-2">
      {/* O título "Comentário oficial" é o da seção: aqui fica só o crédito. */}
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-(--color-text-secondary) text-xs">
          {comment === null
            ? 'esta questão ainda não tem — escreva para criar'
            : comment.author
              ? `por ${comment.author} — salvar passa o crédito para você`
              : 'sem autor — salvar credita você'}
        </span>
      </div>

      {/* O gate é `loaded`, NÃO `raw.isLoading`: o `RichTextEditor` só lê
          `value` no mount (o TipTap guarda `content` uma vez), e a semeadura do
          texto acontece num efeito — ou seja, um render DEPOIS de `isLoading`
          virar `false`. Montar aqui pelo `isLoading` montava o editor com
          string vazia e o `setText` seguinte não tinha mais como entrar: o
          campo abria EM BRANCO numa questão que tem comentário, e a primeira
          tecla digitada substituía o comentário inteiro ao salvar. `loaded`
          vira `true` no mesmo efeito (e no mesmo batch) que semeia o texto,
          então o editor nasce já com o conteúdo certo. */}
      {comment !== null && raw.isError ? (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(
            raw.error,
            'Não foi possível carregar o comentário desta questão.',
          )}{' '}
          Feche e abra a edição para tentar de novo — salvar agora apagaria o
          texto que está gravado.
        </p>
      ) : !loaded ? (
        <p className="text-(--color-text-secondary) text-sm">
          Carregando o comentário…
        </p>
      ) : (
        <>
          <HarrisonCommentAssistant
            questionId={questionId}
            onGenerated={onGenerated}
            onGeneratingChange={onGeneratingChange}
            disabled={harrisonDisabled}
          />
          <RichTextEditor
            key={editorKey}
            label=""
            value={text}
            onChange={onEditorChange}
            onPickImage={pickImage}
          />
          {/* Apagar o texto e salvar remove o comentário — dizer isso aqui
              evita a pergunta "como eu tiro esse comentário?". */}
          {comment !== null && (
            <p className="text-(--color-text-secondary) text-xs">
              Apagar todo o texto e salvar remove o comentário desta questão.
            </p>
          )}
        </>
      )}

      {draftIssue && (
        <p className="text-sm text-red-600" role="alert">
          {draftIssue}
        </p>
      )}
      {issue && (
        <p className="text-sm text-red-600" role="alert">
          {issue}
        </p>
      )}
      {save.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(save.error, 'Falha ao salvar o comentário')}
        </p>
      )}

      <ConfirmModal
        open={pendingDraft !== null}
        onClose={() => setPendingDraft(null)}
        onConfirm={() => {
          if (pendingDraft !== null) applyDraft(pendingDraft);
          setPendingDraft(null);
        }}
        title="Substituir pelo rascunho do Harrison?"
        description="O texto que está no editor sai e entra o rascunho, sem como desfazer. Cancelar descarta o rascunho. Nada é gravado até você salvar."
        confirmLabel="Substituir"
      />
    </div>
  );
}

/**
 * Tem algo no editor? Figura conta: o `richTextIsEmpty` tira todas as tags, e
 * um comentário que é só imagem passaria por vazio — o rascunho entraria por
 * cima dele sem confirmação.
 */
function hasContent(html: string): boolean {
  return !richTextIsEmpty(html) || /<img\b/i.test(html);
}
